import React, { useMemo } from "react";

import {
  TaskPriority,
  TaskStatus,
} from "@fartherfinance/frontend/api/Tasks/Types";

import {
  createVirtualTaskId,
  VirtualTask,
} from "@src/multiCustodian/pages/Advisor/components/VirtualSubtasks/types";

import { CHILDREN_CATALOG, ROOT_TASK_DEFINITIONS } from "./constants";
import VirtualSubtasks from "./VirtualSubtasks";

// --- Helpers --------------------------------------------------------------
const generateId = (): string => Math.random().toString(36).substring(2, 10);

const createTask = (
  title: string,
  isRoot: boolean,
  children: VirtualTask[] = [],
  id?: string
): VirtualTask => ({
  id: createVirtualTaskId(id ?? generateId()),
  title,
  taskStatus: TaskStatus.enum.NOT_STARTED,
  children,
  isRoot,
  description: "",
  dueDate: null,
  taskPriority: TaskPriority.enum.MEDIUM,
});

const generateChildrenForRoot = (rootTitle: string): VirtualTask[] => {
  const pool = CHILDREN_CATALOG[rootTitle] ?? [];
  const count = Math.floor(Math.random() * 5) + 3; // 3–7
  return pool.slice(0, count).map((name) => createTask(name, false));
};

// Returns the five predefined root tasks. Each root task starts without
// children – they will be generated lazily when the user expands/clicks on a
// task (handled inside VirtualSubtasks).
const createInitialRootTasks = (): VirtualTask[] =>
  ROOT_TASK_DEFINITIONS.map((def) =>
    createTask(def.title, true, generateChildrenForRoot(def.title), def.id)
  );

// -------------------------------------------------------------------------
export const createInitialVirtualTasks = (): VirtualTask[] =>
  createInitialRootTasks();

interface Props {
  parentTitle: string;
  tasks: VirtualTask[];
  onChange: (tasks: VirtualTask[]) => void;
}

const TransitionVirtualSubtasksSection: React.FC<Props> = ({
  parentTitle,
  tasks,
  onChange,
}) => {
  // Memoised value ensures the same object reference unless the dependency
  // changes, preventing unnecessary re-renders further down the tree.
  const tasksValue = useMemo(() => tasks, [tasks]);

  return (
    <VirtualSubtasks
      tasks={tasksValue}
      onTasksChange={onChange}
      allowAdd={false}
      parentTitle={parentTitle}
    />
  );
};

export default TransitionVirtualSubtasksSection;
